function getConfiguredBackendOrigin() {
  return (import.meta.env.VITE_BACKEND_URL || "").trim().replace(/\/$/, "")
}

export function getBackendOrigin() {
  return getConfiguredBackendOrigin() || getDefaultBackendOrigin()
}

function getDefaultBackendOrigin() {
  if (import.meta.env.DEV) {
    return "http://localhost:8000"
  }

  return window.location.origin.replace(/\/$/, "")
}

export function getFrontendAppOrigin() {
  const frontendOrigin = new URL(window.location.origin)
  const backendOrigin = getConfiguredBackendOrigin()

  if (import.meta.env.DEV && backendOrigin) {
    frontendOrigin.hostname = new URL(backendOrigin).hostname
  }

  return frontendOrigin.origin
}

export function getApiUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  return `${getBackendOrigin()}${normalizedPath}`
}

// Endpoints where a 401 is an expected answer (session checks, login/logout flows),
// not a signal that the user must re-authenticate.
const authStatusPathPrefixes = ["/api/auth/", "/auth/"]

export class AuthRedirectError extends Error {
  constructor() {
    super("Authentication required.")
    this.name = "AuthRedirectError"
  }
}

export async function apiFetch(path: string, init: RequestInit = {}) {
  const response = await fetch(getApiUrl(path), {
    credentials: "include",
    ...init,
  })

  if (response.status === 401 && !isAuthStatusPath(path)) {
    const { getLoginHref } = await import("@/lib/auth")
    window.location.assign(getLoginHref(`${window.location.pathname}${window.location.search}`))
    throw new AuthRedirectError()
  }

  return response
}

function isAuthStatusPath(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  return authStatusPathPrefixes.some((prefix) => normalizedPath.startsWith(prefix))
}

export async function readJsonResponse(response: Response) {
  const contentType = response.headers.get("content-type") || ""
  if (!contentType.includes("application/json")) {
    return null
  }

  return response.json()
}

export function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

export function getApiErrorMessage(data: unknown, fallback = "Request failed.") {
  if (!data || typeof data !== "object") {
    return fallback
  }

  const responseData = data as { error?: unknown; errors?: unknown }
  if (typeof responseData.error === "string") {
    return responseData.error
  }

  if (responseData.errors && typeof responseData.errors === "object") {
    const fieldErrors = Object.values(responseData.errors as Record<string, unknown>)
    const firstError = fieldErrors.find((fieldError) => typeof fieldError === "string")
    if (typeof firstError === "string") {
      return firstError
    }
  }

  return fallback
}

export function getCsrfToken() {
  const prefix = "csrftoken="

  for (const cookie of document.cookie.split(";")) {
    const trimmedCookie = cookie.trim()
    if (trimmedCookie.startsWith(prefix)) {
      return decodeURIComponent(trimmedCookie.slice(prefix.length))
    }
  }

  return ""
}
